import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningIcon from '@mui/icons-material/Warning';
import SendIcon from 'src/assets/images/icons/send.svg';
import ClockIcon from 'src/assets/images/icons/clock.svg';
import { CourierInvoiceDisputeDto, CreditNoteDto, InvoiceDisputeDto, InvoiceSummaryDto } from 'src/generated/services/TFinancialApi';
import { DeviationWorkflowAction } from 'src/infra/types/deviation';
import { ModalConfirm, ModalInfo } from 'src/presentations/components/molecules/modal';
import { useGetDisputeCreditNote, useInvoiceSummaryFromParams, usePerformDeviationWorkflowAction } from 'src/use-cases/invoice/useInvoices';
import { useReadyForPayment } from 'src/use-cases/invoice/useReadyForPayment';

import { DisputeAgreement, DisputeExpectedAmount } from 'src/utils/options';
import { isAnyShipmentUnderReview } from 'src/utils/invoiceStatus';

import { useInvoiceContext } from 'src/presentations/components/organisms/actionDetails/InvoiceContextProvider';
import { useToastResult } from 'src/presentations/components/molecules/toast/useToastResult';
import { useShipmentSelector } from '../ShipmentSelector';
import { DisputeModal } from '../dispute/DisputeModal';
import { useDisputeSelector } from '../dispute/Selector';

/**
 * if is (Lump Sum resolution or Commercial Settlement) and Credit Note detail has total credit amount => true
 * if is Breakdown resolution and Credit Note detail has currency exchange rates => true
 * if no Credit Note number => false
 * @param invoiceDispute
 * @param creditNoteDetail
 * @returns
 */
const checkAdditionalInformation = (invoiceDispute: InvoiceDisputeDto | CourierInvoiceDisputeDto, creditNoteDetail: CreditNoteDto) => {
  if (!invoiceDispute) {
    return false;
  }
  const { agreementResolution, deviationResolutionType } = invoiceDispute;

  if (!creditNoteDetail) {
    return false;
  }
  const { creditNoteNumber, totalCreditAmountAfterTax, currencyExchangeRates } = creditNoteDetail;

  const hasCreditNoteNumber = !!creditNoteNumber;
  if (!hasCreditNoteNumber) {
    return hasCreditNoteNumber;
  }

  const creditNoteAndLumpSum = agreementResolution === DisputeAgreement.AMEND_INVOICE_CREDIT_NOTE && deviationResolutionType === DisputeExpectedAmount.LUMP_SUM;
  const isLumpSum = creditNoteAndLumpSum || agreementResolution === DisputeAgreement.COMMERCIAL_SETTLEMENT_CREDIT_NOTE;
  const hasLumpSumDetail = isLumpSum && !!totalCreditAmountAfterTax;

  const isBreakDown =
    agreementResolution === DisputeAgreement.AMEND_INVOICE_CREDIT_NOTE && deviationResolutionType === DisputeExpectedAmount.BREAK_DOWN_PER_DEVIATION;
  const hasBreakDownDetail = isBreakDown && !!currencyExchangeRates;

  return hasLumpSumDetail || hasBreakDownDetail;
};

export const ActionDetailMainActions = () => {
  const { data: invoiceSummary } = useInvoiceSummaryFromParams();
  const { shipment: shipmentDetail } = useShipmentSelector();
  const { showDispute, setIsLoadingDispute } = useDisputeSelector();
  const { showToastResult } = useToastResult();
  const navigate = useNavigate();
  const statusName = invoiceSummary?.status;
  const enableGetCreditNote = !!invoiceSummary?.invoiceDisputeId && statusName === 'PENDING_DOCUMENT';
  const { data: creditNoteDetail } = useGetDisputeCreditNote(invoiceSummary?.invoiceDisputeId, enableGetCreditNote);
  const invoiceDispute = invoiceSummary?.invoiceDispute;

  const hasAdditionalInformation = checkAdditionalInformation(invoiceDispute, creditNoteDetail);
  const hasShipmentUnderReview = isAnyShipmentUnderReview(invoiceSummary);

  const [actionType, setActionType] = useState<InvoiceSummaryDto['status']>(null);

  const [isShowActionConfirm, setIsShowActionConfirm] = useState<boolean>(false);
  const [isShowUploadCreditNoteConfirm, setIsShowUploadCreditNoteConfirm] = useState<boolean>(false);
  const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
  const [isDisableAction, setIsDisableAction] = useState<boolean>(false);
  const [isShowActionResult, setIsShowActionResult] = useState<boolean>(false);

  const creditNoteFile = invoiceDispute?.creditNoteFile;

  const hasPermissionCreditNote = invoiceSummary?.permissions?.includes('CAN_GIVE_DISPUTE_REVIEW');

  const { nextWorkflowAction, currentDeviationWorkflow, status } = invoiceSummary;
  const isDispute = useMemo(
    () => nextWorkflowAction === DeviationWorkflowAction.START_DISPUTE || invoiceDispute?.status === 'DRAFT',
    [invoiceDispute?.status, nextWorkflowAction]
  );

  const isNotDisputeWorkflow =
    nextWorkflowAction !== DeviationWorkflowAction.START_DISPUTE && currentDeviationWorkflow !== 'DISPUTE_REVIEW' && invoiceDispute?.status !== 'DRAFT';

  const canContinueDispute = invoiceSummary.invoiceDispute?.canContinueDispute ?? true;

  const isCreditNoteWorkflow =
    status === 'PENDING_DOCUMENT' &&
    (invoiceDispute?.agreementResolution === 'AMEND_INVOICE_CREDIT_NOTE' || invoiceDispute?.agreementResolution === 'COMMERCIAL_SETTLEMENT_CREDIT_NOTE');

  const isHaveRejectDone = shipmentDetail && shipmentDetail?.deviations?.find((item) => item?.status === 'REJECTED_DONE');
  const isHaveNewCharge = shipmentDetail && shipmentDetail?.deviations?.find((item) => item?.type === 'NEW_CHARGE');
  // const isHaveUnfilled = shipmentDetail && shipmentDetail?.deviations?.find((item) => item?.status === 'UNFILLED');
  const isProcReview = currentDeviationWorkflow === 'PROCUREMENT_REVIEW';
  const isOpsReview = currentDeviationWorkflow === 'OPERATION_REVIEW';

  const onClickApprove = () => {
    setActionType('TO_APPROVE');
    setIsShowActionConfirm(true);
  };

  /**
   * Redirect to Audit dashboard
   */
  const goBack = useCallback(() => {
    navigate(`/dashboard?provider=${invoiceSummary?.serviceProvider}&modality=${invoiceSummary?.modality}`);
  }, [navigate, invoiceSummary?.modality, invoiceSummary?.serviceProvider]);

  /**
   * onCloseResult
   */
  const onCloseResult = () => {
    setActionType(null);
    setIsShowActionResult(false);
    goBack();
  };

  const { mutateAsync: performDeviationWorkflowAction, isLoading } = usePerformDeviationWorkflowAction();

  const { invoiceId, checkVersion } = useInvoiceContext();

  /**
   * onErrorVersion
   */
  const onErrorVersion = () => {
    setIsDisableAction(true);
  };

  const sendToProcurement = useCallback(async () => {
    checkVersion(async () => {
      await performDeviationWorkflowAction({ invoiceId, deviationWorkflowAction: DeviationWorkflowAction.SEND_TO_PROCUREMENT });
      goBack();
    }, onErrorVersion);
  }, [checkVersion, invoiceId, performDeviationWorkflowAction, goBack]);

  const sendToOperation = useCallback(async () => {
    checkVersion(async () => {
      await performDeviationWorkflowAction({ invoiceId, deviationWorkflowAction: DeviationWorkflowAction.SEND_TO_OPERATION });
      goBack();
    }, onErrorVersion);
  }, [checkVersion, invoiceId, performDeviationWorkflowAction, goBack]);

  const sendToFinance = useCallback(async () => {
    checkVersion(async () => {
      await performDeviationWorkflowAction({ invoiceId, deviationWorkflowAction: DeviationWorkflowAction.SEND_TO_FINANCE });
      goBack();
    }, onErrorVersion);
  }, [checkVersion, invoiceId, performDeviationWorkflowAction, goBack]);

  // Use the new hook for ready for payment functionality
  const { isLoading: isLoadingPayment, handleReadyForPayment: readyForPayment } = useReadyForPayment({
    setIsShowConfirm: setIsShowActionConfirm,
    onNotAsync: () => {
      showToastResult(
        invoiceSummary?.id,
        DeviationWorkflowAction.SEND_TO_OPERATION,
        invoiceSummary?.invoiceNumber,
        shipmentDetail?.shipmentDetail?.shipmentNumber
      );
      setIsShowActionResult(true);
      setIsShowActionConfirm(false);
    },
  });

  const startDispute = useCallback(async () => {
    if (nextWorkflowAction === DeviationWorkflowAction.START_DISPUTE) {
      checkVersion(async () => {
        setIsLoadingDispute(true);
        showDispute();
        await performDeviationWorkflowAction({ invoiceId, deviationWorkflowAction: DeviationWorkflowAction.START_DISPUTE });
        setIsLoadingDispute(false);
      }, onErrorVersion);
    } else {
      showDispute();
    }
  }, [checkVersion, invoiceId, nextWorkflowAction, performDeviationWorkflowAction, showDispute, setIsLoadingDispute]);

  const markInvoiceToApprove = useCallback(async () => {
    await performDeviationWorkflowAction({ invoiceId, deviationWorkflowAction: DeviationWorkflowAction.MARK_INVOICE_TO_APPROVE });
  }, [invoiceId, performDeviationWorkflowAction]);

  const rejectInvoice = useCallback(async () => {
    await performDeviationWorkflowAction({ invoiceId, deviationWorkflowAction: DeviationWorkflowAction.REJECT_INVOICE });
    showToastResult(
      invoiceSummary?.id,
      DeviationWorkflowAction.SEND_TO_OPERATION,
      invoiceSummary?.invoiceNumber,
      shipmentDetail?.shipmentDetail?.shipmentNumber
    );
  }, [
    performDeviationWorkflowAction,
    invoiceId,
    showToastResult,
    invoiceSummary?.id,
    invoiceSummary?.invoiceNumber,
    shipmentDetail?.shipmentDetail?.shipmentNumber,
  ]);

  const onSubmitAccept = () => {
    checkVersion(
      async () => {
        try {
          if (actionType === 'TO_APPROVE') {
            await readyForPayment();
            setIsShowActionConfirm(false);
          } else {
            await rejectInvoice();

            setIsShowActionConfirm(false);
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log('Error when ready for payment', e);
        }
      },
      () => {
        setIsShowActionConfirm(false);
        setIsDisableAction(true);
      }
    );
  };

  const onSubmitCreditNoteAccept = () => {
    checkVersion(
      async () => {
        setIsActionLoading(true);
        await markInvoiceToApprove();
        setIsActionLoading(false);
        setIsShowUploadCreditNoteConfirm(false);
        goBack();
      },
      () => {
        setIsShowUploadCreditNoteConfirm(false);
        setIsDisableAction(true);
      }
    );
  };

  if (hasShipmentUnderReview) {
    return null;
  }

  return (
    <>
      {(nextWorkflowAction === DeviationWorkflowAction.SEND_TO_PROCUREMENT || (isOpsReview && isHaveNewCharge && !isDispute)) &&
        nextWorkflowAction !== DeviationWorkflowAction.READY_FOR_PAYMENT && (
          <Button
            endIcon={<img src={SendIcon} alt='Send Icon' />}
            disabled={nextWorkflowAction !== DeviationWorkflowAction.SEND_TO_PROCUREMENT || isDisableAction}
            variant='contained'
            color='primary'
            sx={{
              padding: '0.25rem 1rem ',
              textTransform: 'capitalize',
            }}
            onClick={() => sendToProcurement()}
          >
            Send to Procurement
          </Button>
        )}

      {(nextWorkflowAction === DeviationWorkflowAction.SEND_TO_OPERATION || (isProcReview && isHaveRejectDone)) && (
        <Button
          endIcon={<img src={SendIcon} alt='Send Icon' />}
          disabled={nextWorkflowAction !== DeviationWorkflowAction.SEND_TO_OPERATION || isDisableAction}
          variant='contained'
          color='primary'
          sx={{
            padding: '0.25rem 1rem ',
            textTransform: 'capitalize',
          }}
          onClick={() => sendToOperation()}
        >
          Send to Logistics
        </Button>
      )}

      {nextWorkflowAction === DeviationWorkflowAction.SEND_TO_FINANCE && (
        <Button
          endIcon={<ArrowForwardIcon />}
          disabled={isDisableAction}
          variant='contained'
          color='primary'
          sx={{
            padding: '0.25rem 1rem ',
            textTransform: 'capitalize',
          }}
          onClick={() => sendToFinance()}
        >
          Validate
        </Button>
      )}

      {isDispute && (
        <Button
          variant='contained'
          endIcon={<WarningIcon />}
          sx={[
            {
              bgcolor: '#E72547',
              textTransform: 'capitalize',
              fontWeight: '700',
              padding: '0.25rem 1rem ',
              color: '#FFF',
              '&:hover': {
                bgcolor: '#E72547',
                color: '#FFF',
              },
            },
          ]}
          disabled={isNotDisputeWorkflow || (!canContinueDispute && currentDeviationWorkflow === 'DISPUTE_REVIEW') || isDisableAction}
          onClick={startDispute}
        >
          {nextWorkflowAction === DeviationWorkflowAction.START_DISPUTE && 'Start Dispute'}
          {currentDeviationWorkflow === 'DISPUTE_REVIEW' && invoiceDispute?.status === 'DRAFT' && 'Continue Dispute'}
        </Button>
      )}

      {statusName === 'FLAGGED' &&
        ((isProcReview && !isHaveRejectDone) ||
          (isOpsReview && !isHaveNewCharge && !isDispute) ||
          nextWorkflowAction === DeviationWorkflowAction.READY_FOR_PAYMENT) &&
        !isDispute && (
          <Button
            endIcon={<img src={SendIcon} alt='Send Icon' />}
            variant='contained'
            color='primary'
            sx={{
              padding: '0.25rem 1rem ',
              textTransform: 'capitalize',
            }}
            onClick={onClickApprove}
            disabled={nextWorkflowAction !== DeviationWorkflowAction.READY_FOR_PAYMENT || isDisableAction}
          >
            Ready for Payment
          </Button>
        )}

      {isCreditNoteWorkflow && hasPermissionCreditNote && (
        <Button
          endIcon={<img src={ClockIcon} alt='Clock Icon' />}
          variant='contained'
          color='primary'
          sx={{
            padding: '0.25rem 1rem ',
            textTransform: 'capitalize',
          }}
          disabled={!(creditNoteFile && hasAdditionalInformation) || isDisableAction}
          onClick={() => setIsShowUploadCreditNoteConfirm(true)}
        >
          Submit
        </Button>
      )}

      <DisputeModal />

      <ModalConfirm
        open={isShowActionConfirm}
        title='Confirmation'
        isLoading={isLoading || isLoadingPayment}
        onAccept={onSubmitAccept}
        onCancel={() => setIsShowActionConfirm(false)}
      >
        <>
          {actionType === 'TO_REJECT' && <p>Are you sure want to reject this invoice?</p>}
          {actionType === 'TO_APPROVE' && <p>Are you sure want to approve this invoice?</p>}
        </>
      </ModalConfirm>
      <ModalConfirm
        open={isShowUploadCreditNoteConfirm}
        title='Confirmation'
        isLoading={isActionLoading}
        onAccept={onSubmitCreditNoteAccept}
        onCancel={() => setIsShowUploadCreditNoteConfirm(false)}
        buttonAcceptLabel='Submit'
      >
        <p>The uploaded document will be sent to Finance Manager. Are you sure want to submit?</p>
      </ModalConfirm>

      <ModalInfo open={isShowActionResult} title='Success!' onClose={onCloseResult}>
        <>
          {actionType === 'TO_REJECT' && <p>Successfully rejected this invoice. You can now find these invoices in the Rejected tab.</p>}
          {actionType === 'TO_APPROVE' && <p>Successfully approved this invoice. You can now find this invoice in the Report &amp; Anaytics.</p>}
        </>
      </ModalInfo>
    </>
  );
};
